import React, { useState, useCallback } from 'react';
import { TopBar as SharedTopBar, type TopBarAction } from '@ui';
import ChangePasswordModal from './ChangePasswordModal';
import { useNotifications } from '../contexts/NotificationContext';
import { useChat } from '../contexts/ChatContext';
import { useAuth } from '../contexts/AuthContext';
import { ChatPanel } from './ChatPanel';

interface TopBarUnifiedProps {
  logo?: React.ReactNode;
  leftContent?: React.ReactNode;
  centerContent?: React.ReactNode;
  actions?: TopBarAction[];
  showChangePasswordModal: boolean;
  onChangePasswordModalToggle: (show: boolean) => void;
}

/**
 * Unified TopBar wrapper for Zamówienia module
 * 
 * Uses the unified TopBar from @ui with built-in NotificationBell and UserMenu
 * All notifications and user menu actions are unified and consistent
 * 
 * Icons: Module-specific icons should be imported from @ui/icons
 * Example: import { Map, Building, Truck } from '@ui/icons';
 */
const TopBarUnified: React.FC<TopBarUnifiedProps> = ({
  logo,
  leftContent,
  centerContent,
  actions = [],
  showChangePasswordModal,
  onChangePasswordModalToggle
}) => {
  const { notifications, markAsRead, markAllAsRead } = useNotifications();
  const { conversations, unreadCount } = useChat();
  const { user, logout } = useAuth();
  const [chatPanelOpen, setChatPanelOpen] = useState(false);
  const [selectedChatUserId, setSelectedChatUserId] = useState<number | null>(null);

  // Memoize callbacks to prevent unnecessary re-renders
  const handleNotificationClick = useCallback((notification: any) => {
    console.log('[TopBarUnified] Notification clicked:', { id: notification.id, link: notification.link, data: notification.data });
    if (notification.link) {
      try {
        let url = notification.link;
        // Add zamowienie_numer from data field if available (for highlighting order row)
        if (notification.data?.zamowienie_numer) {
          const separator = url.includes('?') ? '&' : '?';
          url += `${separator}zamowienie_numer=${notification.data.zamowienie_numer}`;
        }
        window.location.href = url;
      } catch (e) {
        console.error('Failed to navigate to notification link:', e);
      }
    } else {
      console.log('[TopBarUnified] No link in notification');
    }
  }, []);

  const handleChatConversationClick = useCallback((userId: number) => {
    setSelectedChatUserId(userId);
    setChatPanelOpen(true);
  }, []);

  const handleOpenChat = useCallback(() => {
    setSelectedChatUserId(null);
    setChatPanelOpen(true);
  }, []);

  return (
    <>
      <SharedTopBar
        logo={logo}
        leftContent={leftContent}
        centerContent={centerContent}
        actions={actions}
        notifications={notifications}
        onMarkAsRead={markAsRead}
        onMarkAllAsRead={markAllAsRead}
        onNotificationClick={handleNotificationClick}
        chatConversations={conversations}
        chatUnreadCount={unreadCount}
        onChatConversationClick={handleChatConversationClick}
        onOpenChat={handleOpenChat}
        user={user}
        onLogout={logout}
        onChangePassword={() => onChangePasswordModalToggle(true)}
      />
      
      {/* Change Password Modal */}
      <ChangePasswordModal
        isOpen={showChangePasswordModal}
        onClose={() => onChangePasswordModalToggle(false)}
        onSuccess={() => {
          console.log('Password changed successfully');
        }}
      />

      {/* Chat Panel */}
      <ChatPanel
        isOpen={chatPanelOpen}
        onClose={() => {
          setChatPanelOpen(false);
          setSelectedChatUserId(null);
        }}
        selectedUserId={selectedChatUserId}
        onSelectUser={(userId) => setSelectedChatUserId(userId)}
      />
    </>
  );
};

export default TopBarUnified;

